import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import AuthCard from '@/components/AuthCard'
import { Button } from '@/components/ui/button'
import {
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { authClient } from '@/lib/auth-client'
import { pageLinks, pageMeta } from '@/lib/seo'

export const Route = createFileRoute('/reset-password')({
  // Better Auth's /reset-password/:token callback redirects here with either
  // ?token=<verificationToken> (valid) or ?error=INVALID_TOKEN (invalid or
  // expired) appended to this route's URL.
  validateSearch: (
    search: Record<string, unknown>,
  ): { token?: string; error?: string } => ({
    token:
      typeof search.token === 'string' && search.token.length <= 512
        ? search.token
        : undefined,
    error:
      typeof search.error === 'string' && search.error.length <= 256
        ? search.error
        : undefined,
  }),
  head: () => ({
    // noindex, same reasoning as /login and /signup.
    meta: pageMeta({
      title: 'Reset password · Masklabs',
      description: 'Choose a new password for your Masklabs account.',
      path: '/reset-password',
      noindex: true,
    }),
    links: pageLinks('/reset-password'),
  }),
  component: ResetPasswordPage,
})

function InvalidTokenCard() {
  return (
    <AuthCard>
      <CardHeader>
        <CardTitle className="text-2xl">Link expired</CardTitle>
        <CardDescription>
          This password reset link is invalid or has expired.
        </CardDescription>
      </CardHeader>
      <CardFooter className="flex-col items-stretch gap-3">
        <Button asChild className="w-full">
          <Link to="/forgot-password">Request a new link</Link>
        </Button>
        <p className="text-center text-sm text-muted-foreground">
          <Link
            to="/login"
            className="text-foreground underline-offset-4 hover:underline"
          >
            Back to sign in
          </Link>
        </p>
      </CardFooter>
    </AuthCard>
  )
}

function ResetPasswordPage() {
  const { token, error: searchError } = Route.useSearch()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)
  const [done, setDone] = useState(false)
  // A submit-time failure that means the token itself is no good (expired
  // between page load and submission, or already used) — same treatment as
  // arriving with ?error=INVALID_TOKEN.
  const [tokenInvalid, setTokenInvalid] = useState(false)

  if (searchError === 'INVALID_TOKEN' || !token || tokenInvalid) {
    return <InvalidTokenCard />
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }
    setPending(true)
    const { error: resetError } = await authClient.resetPassword({
      newPassword: password,
      token,
    })
    setPending(false)
    if (resetError) {
      if (resetError.code === 'INVALID_TOKEN') {
        setTokenInvalid(true)
        return
      }
      setError(resetError.message ?? "Couldn't reset your password.")
      return
    }
    setDone(true)
  }

  if (done) {
    return (
      <AuthCard>
        <CardHeader>
          <CardTitle className="text-2xl">Password reset</CardTitle>
          <CardDescription>
            Your password has been changed. Sign in with your new password to
            continue.
          </CardDescription>
        </CardHeader>
        <CardFooter className="flex-col items-stretch gap-3">
          <Button asChild className="w-full">
            <Link to="/login">Sign in</Link>
          </Button>
        </CardFooter>
      </AuthCard>
    )
  }

  return (
    <AuthCard>
      <CardHeader>
        <CardTitle className="text-2xl">Reset password</CardTitle>
        <CardDescription>Choose a new password.</CardDescription>
      </CardHeader>
      <form onSubmit={onSubmit} className="flex flex-col gap-6">
        <CardContent>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="password">New password</FieldLabel>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="confirm-password">
                Confirm password
              </FieldLabel>
              <Input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </Field>
            {error ? <p className="text-sm text-destructive">{error}</p> : null}
          </FieldGroup>
        </CardContent>
        <CardFooter className="flex-col items-stretch gap-3">
          <Button type="submit" className="w-full" disabled={pending}>
            {pending ? 'Resetting…' : 'Reset password'}
          </Button>
        </CardFooter>
      </form>
    </AuthCard>
  )
}
